import React, { useState } from 'react';

import {
  defaultInitialsFieldHeightPx,
  defaultInitialsFieldWidthPx,
  defaultSignatureFieldHeightPx,
  defaultSignatureFieldWidthPx,
  Select,
} from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Divider, Grid, IconButton, InputLabel, Typography } from '@mui/material';
import clsx from 'clsx';
import ReactSignatureCanvas from 'react-signature-canvas';
import { AlineInput } from 'src/AlineInput';
import type { AlineDialogProps } from 'src/components';
import { AlineDialog, AlineDialogButtons, Spacer, TabPanel } from 'src/components';
import { AlineTabs } from 'src/components/generic/AlineTabs';
import { ReverseIcon } from 'src/components/icons/ReverseIcon';
import { MobileBottomSheet } from 'src/editor/mobile/MobileBottomSheet';
import { MobileAdoptAndSignBody } from 'src/editor/mobile/signing/MobileAdoptAndSignBody';
import { useLogAndDispatchErrorSnack } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import { Sentry } from 'src/sentry';
import { makeStyles } from 'tss-react/mui';

import { DrawInitialsPreview } from './DrawInitialsPreview';
import { DrawSignaturePreview } from './DrawSignaturePreview';
import { TypedInitialsPreview } from './TypedInitialsPreview';
import { TypedSignaturePreview } from './TypedSignaturePreview';
import { useAdoptUserSignatures } from './use-adopt-user-signatures';
import { useFramedPreview } from './use-framed-preview';

const useStyles = makeStyles()(theme => ({
  // Use marginBottom and marginTop since MUI Grid uses margin left/right styles and we don't want to override those styles.
  container: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  drawItem: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(1),
  },
  drawPreview: {
    marginTop: theme.spacing(2),
  },
  inputLabelWithMargin: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  inputLabelWithoutMargin: {
    ...theme.appTypography.fieldLabel,
  },
  nav: {
    alignItems: 'center',
    display: 'flex',
    height: 48,
    marginBottom: theme.spacing(2),
  },
  paper: {
    width: 600,
  },
  rightNav: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexGrow: 1,
    height: '100%',
    justifyContent: 'flex-end',
  },
  sigCanvas: {
    backgroundColor: theme.appColors.background,
    border: '3px solid #D1D1D1',
    borderRadius: 3,
    boxSizing: 'border-box',
    height: 150,
    width: '100%',
  },
  signaturePreviewCanvas: {
    height: defaultSignatureFieldHeightPx,
    width: defaultSignatureFieldWidthPx,
  },
  spacingRight: {
    paddingRight: theme.spacing(1),
  },
}));

export const signatureDialogOpenVar = makeVar(false);

export function SignatureDialog(
  props: Omit<AlineDialogProps, 'open' | 'onClose' | 'title'>
): JSX.Element | null {
  if (IS_MOBILE) return <MobileSignatureAdoptionSheet />;
  return <DesktopSignatureDialog {...props} />;
}

function MobileSignatureAdoptionSheet(): JSX.Element | null {
  const isOpen = useReactiveVar(signatureDialogOpenVar);
  if (!isOpen) return null;
  return (
    <MobileBottomSheet onDismiss={() => signatureDialogOpenVar(false)}>
      <MobileAdoptAndSignBody
        buttonLabel="Adopt Signatures"
        onAdopted={() => {
          signatureDialogOpenVar(false);
        }}
      />
    </MobileBottomSheet>
  );
}

function DesktopSignatureDialog(
  props: Omit<AlineDialogProps, 'open' | 'onClose' | 'title'>
): JSX.Element {
  const { classes } = useStyles();
  const dispatchErrorNotification = useLogAndDispatchErrorSnack({
    message: 'An error occurred capturing signatures.',
  });
  const isSignatureDialogOpen = useReactiveVar(signatureDialogOpenVar);
  const { adopt, submitting } = useAdoptUserSignatures();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [typedInitials, setTypedInitials] = useState<string>('');
  const [typedSignature, setTypedSignature] = useState<string>('');
  const [drawSigDataUrl, setDrawSigDataUrl] = useState<string | null>(null);
  const [drawInitialsDataUrl, setDrawInitialsDataUrl] = useState<string | null>(null);
  const [typedSignaturePreviewCanvas, setTypedSignaturePreviewCanvas] =
    useState<HTMLCanvasElement | null>(null);
  const [typedInitialsPreviewCanvas, setTypedInitialsPreviewCanvas] =
    useState<HTMLCanvasElement | null>(null);
  const [drawSigPreviewCanvas, setDrawSigPreviewCanvas] = useState<HTMLCanvasElement | null>(null);
  const [drawInitialsPreviewCanvas, setDrawInitialsPreviewCanvas] =
    useState<HTMLCanvasElement | null>(null);
  const [drawSignatureCanvas, setDrawSignatureCanvas] = useState<ReactSignatureCanvas | null>(null);
  const [drawInitialsCanvas, setDrawInitialsCanvas] = useState<ReactSignatureCanvas | null>(null);

  useFramedPreview({
    canvas: typedSignaturePreviewCanvas,
    enabled: activeTab === 0,
    fallbackText: 'Type Signature',
    hPx: defaultSignatureFieldHeightPx,
    textOrDataUrl: typedSignature,
    type: 'text',
    wPx: defaultSignatureFieldWidthPx,
  });
  useFramedPreview({
    canvas: typedInitialsPreviewCanvas,
    enabled: activeTab === 0,
    fallbackText: 'TS',
    hPx: defaultInitialsFieldHeightPx,
    textOrDataUrl: typedInitials,
    type: 'text',
    wPx: defaultInitialsFieldWidthPx,
  });
  useFramedPreview({
    canvas: drawSigPreviewCanvas,
    fallbackText: 'Draw Signature',
    hPx: defaultSignatureFieldHeightPx,
    textOrDataUrl: drawSigDataUrl ?? '',
    type: drawSigDataUrl ? 'dataUrl' : 'text',
    wPx: defaultSignatureFieldWidthPx,
  });
  useFramedPreview({
    canvas: drawInitialsPreviewCanvas,
    fallbackText: 'DS',
    hPx: defaultInitialsFieldHeightPx,
    textOrDataUrl: drawInitialsDataUrl ?? '',
    type: drawInitialsDataUrl ? 'dataUrl' : 'text',
    wPx: defaultInitialsFieldWidthPx,
  });

  const noTypedSignature = !typedSignature || !typedInitials;
  const noDrawnSignature = !drawSigDataUrl || !drawInitialsDataUrl;
  const isSubmitDisabled =
    (activeTab === 0 && noTypedSignature) || (activeTab === 1 && noDrawnSignature);

  async function saveUserSignatures(): Promise<void> {
    const signaturePreviewCanvas =
      activeTab === 0 ? typedSignaturePreviewCanvas : drawSigPreviewCanvas;
    const initialsPreviewCanvas =
      activeTab === 0 ? typedInitialsPreviewCanvas : drawInitialsPreviewCanvas;
    if (!signaturePreviewCanvas || !initialsPreviewCanvas) return;
    await adopt({ initialsCanvas: initialsPreviewCanvas, signatureCanvas: signaturePreviewCanvas });
    closeSignatureDialog();
  }

  function resetDialogState(): void {
    setTypedSignature('');
    setTypedInitials('');
    drawSignatureCanvas?.clear();
    drawInitialsCanvas?.clear();
    setDrawSigDataUrl(null);
    setDrawInitialsDataUrl(null);
  }

  function closeSignatureDialog(): void {
    signatureDialogOpenVar(false);
    // setTimeout so there is no flicker in the modal UI before it closes...
    setTimeout(() => {
      resetDialogState();
      setActiveTab(0);
    }, 100);
  }

  return (
    <AlineDialog
      classes={{ paper: classes.paper }}
      open={isSignatureDialogOpen}
      title="Adopt signatures"
      xDataCy={Select.dialog.signatureDialog.closeXButton}
      onClose={closeSignatureDialog}
      {...props}
    >
      <div className={classes.nav}>
        <AlineTabs<number>
          tabs={[
            { label: 'Type', value: 0 },
            { label: 'Draw', value: 1 },
          ]}
          value={activeTab}
          onChange={value => {
            setActiveTab(value);
            resetDialogState();
          }}
        />
        <div className={classes.rightNav} />
      </div>
      <TabPanel tabValue={0} value={activeTab}>
        <>
          <Grid container className={classes.container} spacing={3}>
            <Grid size={6}>
              <InputLabel className={classes.inputLabelWithMargin}>Your name</InputLabel>
              <AlineInput
                fullWidth
                data-cy={Select.dialog.signatureDialog.typedSignatureInput}
                inputProps={{ 'aria-label': 'Your name' }}
                value={typedSignature}
                onChange={event => setTypedSignature(event.target.value)}
              />
            </Grid>
            <Grid size={4}>
              <InputLabel className={classes.inputLabelWithMargin}>Your initials</InputLabel>
              <AlineInput
                fullWidth
                data-cy={Select.dialog.signatureDialog.typedInitialsInput}
                inputProps={{ 'aria-label': 'Your initials' }}
                value={typedInitials}
                onChange={event => setTypedInitials(event.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TypedSignaturePreview
                setTypedSignaturePreviewCanvas={setTypedSignaturePreviewCanvas}
                typedSignature={typedSignature}
              />
            </Grid>
            <Grid size={4}>
              <TypedInitialsPreview
                setTypedInitialsPreviewCanvas={setTypedInitialsPreviewCanvas}
                typedInitials={typedInitials}
              />
            </Grid>
          </Grid>
          <Divider variant="fullWidth" />
        </>
      </TabPanel>
      <TabPanel tabValue={1} value={activeTab}>
        <>
          <Grid container className={classes.container}>
            <Grid className={clsx(classes.drawItem, classes.spacingRight)} size={8}>
              <InputLabel className={classes.inputLabelWithoutMargin}>Draw signature</InputLabel>
              <Spacer />
              <IconButton
                size="small"
                onClick={() => {
                  drawSignatureCanvas?.clear();
                  setDrawSigDataUrl(null);
                }}
              >
                <ReverseIcon />
              </IconButton>
            </Grid>
            <Grid className={classes.drawItem} size={4}>
              <InputLabel className={classes.inputLabelWithoutMargin}>Draw initials</InputLabel>
              <Spacer />
              <IconButton
                size="small"
                onClick={() => {
                  drawInitialsCanvas?.clear();
                  setDrawInitialsDataUrl(null);
                }}
              >
                <ReverseIcon />
              </IconButton>
            </Grid>
            <Grid className={classes.spacingRight} size={8}>
              <ReactSignatureCanvas
                ref={setDrawSignatureCanvas}
                canvasProps={{ className: classes.sigCanvas }}
                onEnd={async () => {
                  if (!drawSignatureCanvas) return;

                  try {
                    setDrawSigDataUrl(drawSignatureCanvas.toDataURL('image/png'));
                  } catch (e) {
                    Sentry.captureException(e);
                    dispatchErrorNotification(e);
                  }
                }}
              />
            </Grid>
            <Grid size={4}>
              <ReactSignatureCanvas
                ref={setDrawInitialsCanvas}
                canvasProps={{ className: classes.sigCanvas }}
                onEnd={async () => {
                  if (!drawInitialsCanvas) return;

                  try {
                    setDrawInitialsDataUrl(drawInitialsCanvas.toDataURL('image/png'));
                  } catch (e) {
                    Sentry.captureException(e);
                    dispatchErrorNotification(e);
                  }
                }}
              />
            </Grid>
            <Grid className={classes.drawPreview} size={8}>
              <DrawSignaturePreview
                isDrawSigEmpty={!drawSigDataUrl}
                setDrawSigPreviewCanvas={setDrawSigPreviewCanvas}
              />
            </Grid>
            <Grid className={classes.drawPreview} size={4}>
              <DrawInitialsPreview
                isDrawInitialsEmpty={!drawInitialsDataUrl}
                setDrawInitialsPreviewCanvas={setDrawInitialsPreviewCanvas}
              />
            </Grid>
          </Grid>
          <Divider variant="fullWidth" />
        </>
      </TabPanel>
      <SignatureDisclosure />
      <AlineDialogButtons
        cancelHandler={closeSignatureDialog}
        disabled={submitting || isSubmitDisabled}
        loading={submitting}
        submitHandler={saveUserSignatures}
        submitText="Adopt Signatures"
      />
    </AlineDialog>
  );
}

const useSignatureStyles = makeStyles()(theme => ({
  signatureDisclosureText: {
    margin: theme.spacing(1, 1, 0),
  },
}));

function SignatureDisclosure(): JSX.Element {
  const { classes } = useSignatureStyles();

  return (
    <Typography className={classes.signatureDisclosureText}>
      By clicking Adopt, I agree that my signature and initials are the electronic representation of
      my signature on all envelopes and contracts and that my electronic signature is the legal
      equivalent of my manual/handwritten signature.
    </Typography>
  );
}
